import { ChangeEvent, MouseEvent } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CustomerPicker, { CustomerOption } from "../SpecialOrder/CustomerPicker";
import {
  BuyerDraft,
  BuyerSource,
  NoteDraft,
  NoteFormErrorKey,
  NoteFormErrors,
  TAX_ID_LENGTH,
  sanitizeInteger,
} from "./noteDraft";

interface BuyerSectionProps {
  draft: NoteDraft;
  errors: NoteFormErrors;
  /** The branch head office searches customers in; undefined for a branch user. */
  branchUuid?: string;
  isCustomerSearchBlocked: boolean;
  onChange: (patch: Partial<NoteDraft>) => void;
}

const BUYER_SOURCES: { value: BuyerSource; label: string }[] = [
  { value: "CUSTOMER", label: "ลูกค้าในระบบ" },
  { value: "TYPED", label: "กรอกข้อมูลผู้ซื้อ" },
];

interface TypedBuyerField {
  key: keyof BuyerDraft;
  label: string;
  size: number;
  required?: boolean;
  errorKey?: NoteFormErrorKey;
}

/** The server needs a name and an address for a typed buyer; the rest is optional. */
const TYPED_BUYER_FIELDS: TypedBuyerField[] = [
  { key: "name", label: "ชื่อผู้ซื้อ", size: 12, required: true, errorKey: "buyerName" },
  { key: "address", label: "ที่อยู่", size: 12, required: true, errorKey: "buyerAddress" },
  { key: "taxId", label: "เลขประจำตัวผู้เสียภาษี", size: 6, errorKey: "buyerTaxId" },
  { key: "branch", label: "สาขาของผู้ซื้อ", size: 6 },
  { key: "contact", label: "ผู้ติดต่อ", size: 6 },
  { key: "phone", label: "โทรศัพท์", size: 6 },
];

/** A tax id is digits only, at most 13 of them; every other field is free text. */
const typedBuyerValue = (key: keyof BuyerDraft, text: string): string =>
  key === "taxId" ? sanitizeInteger(text).slice(0, TAX_ID_LENGTH) : text;

function TypedBuyerInput({
  field,
  value,
  error,
  onChangeField,
}: Readonly<{
  field: TypedBuyerField;
  value: string;
  error?: string;
  onChangeField: (key: keyof BuyerDraft, text: string) => void;
}>) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChangeField(field.key, event.target.value);

  return (
    <Grid size={{ xs: 12, sm: field.size }}>
      <TextField
        fullWidth
        size="small"
        required={field.required}
        label={field.label}
        value={value}
        onChange={handleChange}
        error={Boolean(error)}
        helperText={error}
      />
    </Grid>
  );
}

/**
 * The buyer of a note keyed in by hand: a customer in the system (the server snapshots its
 * details), or a buyer typed in full. A note linked to a bill takes the bill's buyer instead.
 */
export default function BuyerSection({
  draft,
  errors,
  branchUuid,
  isCustomerSearchBlocked,
  onChange,
}: Readonly<BuyerSectionProps>) {
  const handleSourceChange = (_event: MouseEvent<HTMLElement>, value: BuyerSource | null) => {
    if (value) onChange({ buyerSource: value });
  };
  const handleCustomerChange = (customer: CustomerOption | null) => onChange({ customer });
  const handleBuyerFieldChange = (key: keyof BuyerDraft, text: string) =>
    onChange({ buyer: { ...draft.buyer, [key]: typedBuyerValue(key, text) } });

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={draft.buyerSource}
          onChange={handleSourceChange}
        >
          {BUYER_SOURCES.map((source) => (
            <ToggleButton key={source.value} value={source.value}>
              {source.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Grid>
      {draft.buyerSource === "CUSTOMER" ? (
        <Grid size={{ xs: 12 }}>
          <CustomerPicker
            value={draft.customer}
            onChange={handleCustomerChange}
            branchUuid={branchUuid}
            disabled={isCustomerSearchBlocked}
            error={errors.customer}
            helperText={isCustomerSearchBlocked ? "เลือกสาขาก่อน" : undefined}
          />
        </Grid>
      ) : (
        TYPED_BUYER_FIELDS.map((field) => (
          <TypedBuyerInput
            key={field.key}
            field={field}
            value={draft.buyer[field.key]}
            error={field.errorKey && errors[field.errorKey]}
            onChangeField={handleBuyerFieldChange}
          />
        ))
      )}
    </Grid>
  );
}
